import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";

import { Button } from "../../components/ui/button";
import { ErrorBanner } from "../../features/web3/hyperliquid/components/ErrorBanner";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { inputCls, labelCls } from "../forex/parts";
import { accountLabel, post, useLedger, useLookups } from "./api";
import { AMOUNT_RE, RATE_RE, canSave, isBase, lineDrCr, receiptBody, totals } from "./logic";

// A new row starts in the base currency, whose rate is always 1.
const blankLine = (base) => ({ key: crypto.randomUUID(), account: "", description: "", currency: base, rate: "1", amount: "" });

const newForm = (base) => ({
  fiscalYear: "",
  date: new Date().toLocaleDateString("en-CA"), // local YYYY-MM-DD
  receiptType: "General",
  description: "",
  costCenter: "",
  copyDescription: false,
  // A retry after a timeout reuses this id, so the backend refuses a duplicate.
  requestId: crypto.randomUUID(),
  lines: [blankLine(base), blankLine(base)],
});

// Stored lines carry the currency name in `currency` (and again in
// `currencyName`); the form edits the name and sends it back as such.
const fromReceipt = (r) => ({
  fiscalYear: r.fiscalYear,
  date: r.date.slice(0, 10),
  receiptType: r.receiptType,
  description: r.description,
  costCenter: r.costCenter ?? "",
  copyDescription: false,
  lines: r.lines.map((l) => ({ key: crypto.randomUUID(), ...l, currency: l.currencyName || l.currency })),
});

const cellCls = "w-full rounded border border-border/70 bg-white/[0.03] px-2 py-1.5 text-sm focus:border-ginox-green/50 focus:outline-none";
const bad = (ok, value) => (value && !ok ? " border-red-500/70" : "");

function Select({ value, onChange, options, placeholder, className = cellCls, ...rest }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={className} {...rest}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function VoucherForm({ receipt, lk }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const base = lk.baseCurrency;
  const [form, setForm] = useState(() => (receipt ? fromReceipt(receipt) : newForm(base)));
  const [saving, setSaving] = useState(false);
  const fiscalYear = form.fiscalYear || lk.openYear?._id || "";
  const t = totals(form.lines);
  const ready = canSave(form.lines, base) && fiscalYear && form.date;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setLine = (key, patch) =>
    setForm((f) => ({ ...f, lines: f.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)) }));
  // Picking a currency brings its current rate; the admin may still change it
  // (except for the base currency, fixed at 1).
  const setCurrency = (key, name) => {
    const c = lk.currencies.find((x) => x.name === name);
    setLine(key, { currency: name, rate: c?.isBase || isBase(name, base) ? "1" : (c?.rate ?? "") });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = receiptBody({ ...form, fiscalYear }, receipt);
      const saved = await post(receipt ? `/receipts/${receipt._id}/edit` : "/receipts", body);
      toast.success(`Receipt ${saved.number} ${receipt ? "updated" : "posted"}`);
      qc.invalidateQueries({ queryKey: ["trust-ledger"] });
      navigate("/trust-ledger/receipts");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Only active leaf accounts take postings; group accounts organise the tree.
  const accounts = lk.postable.filter((a) => a.active).map((a) => ({ value: a._id, label: accountLabel(a) }));
  const currencies = lk.currencies
    .filter((c) => c.active)
    .map((c) => ({ value: c.name, label: c.isBase ? `${c.name} (base)` : c.name }));

  return (
    <form onSubmit={submit} className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label className={labelCls}>Fiscal year</label>
          {/* Only an open year takes postings, and an edit cannot move years. */}
          <Select
            className={inputCls}
            value={fiscalYear}
            onChange={(v) => set({ fiscalYear: v })}
            disabled={Boolean(receipt)}
            required
            placeholder="—"
            options={lk.years
              .filter((y) => y.status === "open" || y._id === fiscalYear)
              .map((y) => ({ value: y._id, label: y.title }))}
          />
        </div>
        <div>
          <label className={labelCls}>Date</label>
          <input type="date" required value={form.date} onChange={(e) => set({ date: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Type</label>
          <Select
            className={inputCls}
            value={form.receiptType}
            onChange={(v) => set({ receiptType: v })}
            options={(lk.options?.receiptTypes ?? [form.receiptType]).map((x) => ({ value: x, label: x }))}
          />
        </div>
        <div>
          <label className={labelCls}>Cost center (optional)</label>
          <Select
            className={inputCls}
            value={form.costCenter}
            onChange={(v) => set({ costCenter: v })}
            placeholder="—"
            options={lk.costCenters
              .filter((c) => c.active || c._id === form.costCenter)
              .map((c) => ({ value: c._id, label: `${c.code} · ${c.name}` }))}
          />
        </div>
        <div className="sm:col-span-2 lg:col-span-3">
          <label className={labelCls}>Description</label>
          <input value={form.description} onChange={(e) => set({ description: e.target.value })} className={inputCls} />
        </div>
        <label className="flex items-end gap-2 pb-2 text-sm">
          <input
            type="checkbox"
            checked={form.copyDescription}
            onChange={(e) => set({ copyDescription: e.target.checked })}
          />
          Copy description to all rows
        </label>
      </div>

      <div className="overflow-x-auto rounded-md border border-border/70">
        <table className="w-full min-w-[1000px] text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[1.5px] text-muted-foreground">
              <th className="w-8 px-2 py-2">#</th>
              <th className="px-2 py-2">Account</th>
              <th className="px-2 py-2">Description</th>
              <th className="w-28 px-2 py-2">Currency</th>
              <th className="w-32 px-2 py-2">Rate to {base}</th>
              <th className="w-40 px-2 py-2">Amount (− = credit)</th>
              <th className="w-32 px-2 py-2 text-right">Debit ({base})</th>
              <th className="w-32 px-2 py-2 text-right">Credit ({base})</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {form.lines.map((l, i) => {
              const x = lineDrCr(l.amount, l.rate);
              const lineInBase = isBase(l.currency, base);
              return (
                <tr key={l.key} className="border-t border-border/40">
                  <td className="px-2 py-1.5 text-muted-foreground">{i + 1}</td>
                  <td className="px-2 py-1.5">
                    <Select value={l.account} onChange={(v) => setLine(l.key, { account: v })} options={accounts} placeholder="Select account" required />
                  </td>
                  <td className="px-2 py-1.5">
                    <input
                      value={form.copyDescription ? form.description : l.description}
                      disabled={form.copyDescription}
                      onChange={(e) => setLine(l.key, { description: e.target.value })}
                      className={cellCls}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Select
                      value={l.currency}
                      onChange={(v) => setCurrency(l.key, v)}
                      options={
                        l.currency && !currencies.some((c) => c.value === l.currency)
                          ? [{ value: l.currency, label: `${l.currency} (inactive)` }, ...currencies]
                          : currencies
                      }
                      placeholder="—"
                      required
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <input
                      value={l.rate}
                      readOnly={lineInBase}
                      inputMode="decimal"
                      onChange={(e) => setLine(l.key, { rate: e.target.value.trim() })}
                      className={cellCls + " font-mono" + bad(RATE_RE.test(l.rate) && (!lineInBase || l.rate === "1"), l.rate)}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <input
                      value={l.amount}
                      inputMode="decimal"
                      placeholder="1200 or -1200"
                      onChange={(e) => setLine(l.key, { amount: e.target.value.trim() })}
                      className={cellCls + " font-mono" + bad(AMOUNT_RE.test(l.amount), l.amount)}
                    />
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">{x.debit}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{x.credit}</td>
                  <td className="px-2 py-1.5">
                    {form.lines.length > 2 && (
                      <button
                        type="button"
                        title="Remove row"
                        onClick={() => set({ lines: form.lines.filter((y) => y.key !== l.key) })}
                        className="text-muted-foreground hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <Button
          type="button"
          size="sm"
          disabled={form.lines.length >= 200}
          onClick={() => set({ lines: [...form.lines, blankLine(base)] })}
        >
          <Plus className="h-3.5 w-3.5" /> Add row
        </Button>
        <div className="flex flex-wrap gap-6 font-mono text-sm">
          <span>Total debit: {t.debit} {base}</span>
          <span>Total credit: {t.credit} {base}</span>
          <span className={t.difference === "0" ? "text-emerald-400" : "text-red-400"}>Difference: {t.difference}</span>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button asChild size="sm">
          <Link to="/trust-ledger/receipts">Cancel</Link>
        </Button>
        {/* Writes can take 30s+, so the button stays off until the backend answers. */}
        <Button type="submit" size="sm" variant="default" disabled={!ready || saving}>
          {saving ? "Saving…" : receipt ? "Save changes" : "Save voucher"}
        </Button>
      </div>
    </form>
  );
}

export default function TrustLedgerReceiptForm() {
  const { id } = useParams();
  const lk = useLookups();
  // Edit starts from the stored receipt, including its version for the lock.
  const q = useLedger(id ? `/receipts/${id}` : null, undefined, { placeholderData: undefined });
  // The form's blank rows need the base currency, so it waits for the options.
  const loading = (id && !q.data) || !lk.options;

  return (
    <QueueLayout
      eyebrow="Trust Ledger"
      title={id ? "Edit" : "New"}
      accent="voucher"
      description={`Positive amounts are debits, negative amounts credits; each converts to ${lk.baseCurrency} at its rate. Debits must equal credits to save.`}
      isFetching={q.isFetching}
      onRefresh={() => q.refetch()}
    >
      {q.error ? (
        <ErrorBanner error={q.error} onRetry={() => q.refetch()} />
      ) : loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <VoucherForm key={q.data?.version ?? "new"} receipt={q.data} lk={lk} />
      )}
    </QueueLayout>
  );
}
